// The queue and its acts over the API (docs/04-contracts.md section 7; ADR-031): one query for the
// pack's queue, one mutation for the three acts. An act's answer is the issue as it now stands, and
// goes straight into the cached queue; a 409 means someone else got there first, so the queue is
// fetched again and the panel redraws from what is now true.
import { SourceIssue, SourceIssueQueue, type SourceIssueAnswer } from "@waypoint/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../api/provider.js";
import { ConflictError } from "../api/errors.js";
import { z } from "zod";
import { catalogueOf, withIssue } from "./model.js";

export const issuesKey = (packId: string) => ["issues", packId] as const;

const issuesPath = (packId: string) => `/v1/packs/${encodeURIComponent(packId)}/issues`;

export function useIssueQueue(packId: string) {
  const api = useApi();
  return useQuery({
    queryKey: issuesKey(packId),
    queryFn: () => api.get(issuesPath(packId), SourceIssueQueue),
  });
}

export type IssueAct =
  | { readonly act: "answer"; readonly answer: SourceIssueAnswer }
  | { readonly act: "waive"; readonly reason: string }
  | { readonly act: "reopen"; readonly question: string };

export function useActOnIssue(packId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ issueId, act }: { issueId: string; act: IssueAct }) => {
      const path = `${issuesPath(packId)}/${encodeURIComponent(issueId)}/${act.act}`;
      const body =
        act.act === "answer"
          ? act.answer
          : act.act === "waive"
            ? { reason: act.reason }
            : { question: act.question };
      return api.post(path, SourceIssue, body);
    },
    onSuccess: (issue) => {
      queryClient.setQueryData<SourceIssueQueue>(issuesKey(packId), (queue) =>
        queue === undefined ? queue : withIssue(queue, issue),
      );
    },
    onError: (err) => {
      if (err instanceof ConflictError)
        void queryClient.invalidateQueries({ queryKey: issuesKey(packId) });
    },
  });
}

/**
 * The part of GET /v1/production-plan the queue reads: the catalogue of every imported pack's
 * segments (docs/04-contracts.md section 7). It gives the packs the reviewer can switch between and
 * each linked segment's title. The plan itself is not read here.
 */
const PlanCatalogue = z.object({
  catalogue: z.array(z.object({ packId: z.string(), titleId: z.string(), title: z.string() })),
});

export const planCatalogueKey = ["production-plan", "catalogue"] as const;

export function usePlanCatalogue() {
  const api = useApi();
  return useQuery({
    queryKey: planCatalogueKey,
    queryFn: async () => {
      const { catalogue } = await api.get("/v1/production-plan", PlanCatalogue);
      return catalogueOf(catalogue);
    },
  });
}
